/**
 * Epic 6 server calls: bulk actions (US 6.7) and saved filters (US 6.6, 6.9).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_CALENDAR_PREFIX, SuiteApiClient } from "@weco/suite-client";
import { call, type TaskCard, type TaskPatchBody } from "../task/api";

const client = new SuiteApiClient(API_CALENDAR_PREFIX);

// ── Bulk ─────────────────────────────────────────────────────────────────────

export type BulkPatch = Omit<TaskPatchBody, "assignees"> & {
  assignees?: string[];
  add_assignees?: string[];
  done?: boolean;
};

export type BulkBody =
  | { action: "update"; ids: string[]; patch: BulkPatch }
  | { action: "update"; items: { id: string; patch: BulkPatch }[] }
  | { action: "archive" | "unarchive" | "delete" | "restore"; ids: string[] };

export interface BulkResult {
  updated: TaskCard[];
  failed: { id: string; title: string | null; code: string; detail: string }[];
  /** POST this back to reverse what succeeded. */
  undo: BulkBody | null;
}

export const bulkApi = {
  run: (body: BulkBody) => call(() => client.post<BulkResult>("/task-bulk/", body)),
};

// ── Saved filters ────────────────────────────────────────────────────────────

export type DigestFrequency = "daily" | "weekly";

export interface SavedFilter {
  id: string;
  name: string;
  description: string;
  config: unknown;
  hub: string | null;
  hub_name: string;
  squad: string | null;
  squad_name: string;
  shared_squad: string | null;
  shared_squad_name: string;
  owner_email: string;
  owner_name: string;
  is_owner: boolean;
  can_edit: boolean;
  subscription: DigestFrequency | null;
  created_at: string;
  updated_at: string;
}

export const savedFiltersApi = {
  list: () => call(() => client.get<{ results: SavedFilter[] }>("/saved-filters/")).then((r) => r.results),
  create: (body: { name: string; description?: string; config: unknown; hub?: string | null; squad?: string | null }) =>
    call(() => client.post<SavedFilter>("/saved-filters/", body)),
  update: (id: string, body: Partial<Pick<SavedFilter, "name" | "description" | "config">>) =>
    call(() => client.patch<SavedFilter>(`/saved-filters/${id}/`, body)),
  remove: (id: string) => call(() => client.delete<void>(`/saved-filters/${id}/`)),
  share: (id: string, squad: string | null) => call(() => client.post<SavedFilter>(`/saved-filters/${id}/share/`, { squad })),
  subscribe: (id: string, frequency: DigestFrequency) => call(() => client.put<SavedFilter>(`/saved-filters/${id}/subscription/`, { frequency })),
  unsubscribe: (id: string) => call(() => client.delete<SavedFilter>(`/saved-filters/${id}/subscription/`)),
};

export const SAVED_FILTERS_KEY = ["tasks", "saved-filters"] as const;

export function useSavedFilters() {
  return useQuery({ queryKey: SAVED_FILTERS_KEY, queryFn: savedFiltersApi.list, staleTime: 30_000 });
}

export function useRefreshSavedFilters() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: SAVED_FILTERS_KEY });
}
